/**
 * 流水「父行」派生 —— **唯一实现**（C 域 §5.1.1 · `DR-016` §3.7）
 *
 * 依据：C 域 PRD **§5.1.1**（显示单元 = 交易；父行 = 派生净额行）· **§5.1.2**（方向着色与「不计收支」标注）·
 * `DR-016` **§3.7**（「父行」的技术定义）· A 域 PRD **§5.1**（交易不持方向与金额）· 架构 **§4.1 / §5.2**。
 *
 * ## 🔴 为什么这段逻辑必须是纯函数、且只能有一份
 *
 * 架构 §4.1 的第三条派生铁律：
 *
 * > 「父行」= 该交易在**主账户**上的分组净额（**不新增字段**）
 *
 * ⇒ 它是**派生值**，`transactions` 上**没有、也不得有**任何承接列（`AC-C1.3` 就是拿数据审计这一条）。
 * 而只要它是派生值，就存在「服务端算一遍、Web 算一遍、小程序再算一遍」的**天然诱惑** ——
 * 那正是本项目五个历史病根里的第 ② 与 ⑤ 条（同一份数据两个数据源 / 小程序自己 `reduce`）。
 * 故本文件是**唯一实现**，服务端与两端都调用它（架构 §5.2 手段 1）。
 *
 * ## 父行显示规则（C 域 §5.1.1 权威表）
 *
 * | 交易形态 | 父行显示 |
 * |---|---|
 * | 收入 / 支出（单腿） | 腿金额 + 方向 |
 * | **工资条**（多腿） | **主账户净变动**（`DR-016` §3.3 例：`21,000 − 各扣减 = 14,500 = 父项`） |
 * | **拆分**（多腿同账户、同向） | 各腿合计（`100 = 60 + 40`） |
 * | 转账（2 腿跨账户） | 「转出账户 → 转入账户」（🔴 **不得取净额**，净额为 0 无意义） |
 *
 * ## 主账户怎么定位（本文件把「资金出口恒为 1 个」翻成可执行规则）
 *
 * `DR-016` §3.8③ 定的口径是「**去向自由、出口唯一**」—— 工资条各子项的去向可指向任意账户，
 * 但**资金出口恒为 1 个主账户**，否则「父项 = 实发」这条银行对账锚点当场失效。
 * 反映到腿集合上，**出口账户恰好就是那个「同时有 `in` 腿与 `out` 腿」的账户**：
 *
 * - 工资条：工资卡 = `in`（税前）+ 多条 `out`（社保 / 个税 / 税后去向）；社保账户**只有 `in`** ⇒ 唯一命中；
 * - 拆分 / 单腿：只有一个账户 ⇒ 自然命中；
 * - 转账：两个账户**各自方向单一** ⇒ **不命中** ⇒ 走转账分支（这正是它该去的地方）。
 *
 * ⚠️ **退化形态**（命中 0 个或 > 1 个）**不抛错、不静默**：取「腿数最多者、并列取 `accountId` 字典序最小」
 * 作为主账户，并把 `ambiguous = true` 带出去。理由与 `DR-012`「曲线只用真实快照」同源 ——
 * 宁可让消费端显示一个**可解释的退化标记**，也不要「悄悄算出一个数」或「整页 500」。
 */

import type { Cents } from '../types/brand.js';
import type { LegDirection } from '../enums/journal.js';
import type { CategoryKind } from '../enums/platform.js';
import { sumCents, toCents } from './money.js';

// ───────────────────────────── 输入 ─────────────────────────────

/**
 * 父行派生所需的一条分录腿（最小子集）。
 *
 * ⚠️ `currency` 由数据层 join 账户表得到 —— **腿自身不挂币种列**（架构 §4.1）；
 * `txnExcludeFromStats` 是其**所属交易**的标记（`transactions.excludeFromStats`）。
 * 两者都传进来、由本文件做 OR，是为了让「有效的不计收支」这个判定**只有一个地方** ——
 * 若让调用方各自 `||` 一遍，就是同一规则的第二份载体。
 */
export interface RowLeg {
  id: string;
  accountId: string;
  /** 腿所属账户的币种（ISO 4217） */
  currency: string;
  direction: LegDirection;
  /** **恒为正整数分**（规格 §22.9：绝不用负数 `out`） */
  amount: Cents;
  categoryId: string | null;
  categoryKind: CategoryKind | null;
  /** 腿自身的不计收支标记（`legs.excludeFromStats`） */
  excludeFromStats: boolean;
  /** 其交易的不计收支标记（`transactions.excludeFromStats`） */
  txnExcludeFromStats: boolean;
}

// ───────────────────────────── 输出 ─────────────────────────────

/** 父行的展示形态（**派生量**，不是存储枚举 —— 与 `UNCATEGORIZED_SENTINEL` 同类） */
export const ParentRowKind = {
  /** 单腿（普通收入 / 支出） */
  Single: 'single',
  /** 多腿 —— 主账户净变动（工资条 / 拆分 / 其它同账户组合） */
  AccountNet: 'accountNet',
  /** 转账 —— 跨账户资金移动，**不取净额** */
  Transfer: 'transfer',
} as const;
export type ParentRowKind = (typeof ParentRowKind)[keyof typeof ParentRowKind];

/** 父行方向（`flat` = 主账户净变动为 0） */
export type RowDirection = 'in' | 'out' | 'flat';

/** 转账形态的两端（跨币种下 `from` / `to` 币种**可以不同** ⇒ 各自带币种与金额） */
export interface TransferRow {
  fromAccountId: string;
  fromCurrency: string;
  /** 转出侧合计（该账户上的 `out` 腿之和） */
  fromAmount: Cents;
  toAccountId: string;
  toCurrency: string;
  /** 转入侧合计（该账户上的 `in` 腿之和） */
  toAmount: Cents;
  /**
   * 两侧是否同币种。
   * ⚠️ **跨币种时两侧金额本就不相等**（`DR-004`：隐含汇率是派生量、不存储）
   * ⇒ 消费端**不得**用「两金额不等」判定数据异常。
   */
  sameCurrency: boolean;
}

/**
 * 派生出的父行。
 *
 * 🔴 **本结构每一个字段都不落库** —— 它是查询响应的一部分（`AC-C1.3`）。
 */
export interface ParentRow {
  kind: ParentRowKind;
  /** 主账户；**转账形态为 `null`**（转账不取净额，没有"主账户"这回事） */
  mainAccountId: string | null;
  /** 主账户币种；转账形态 = **转出**账户币种（展示「转出」一侧的锚点） */
  currency: string;
  /** 主账户净腿变动（正 = 净流入，负 = 净流出）；**转账形态为 `null`** */
  net: Cents | null;
  /** 方向（`kind='transfer'` 时为 `from` 腿的方向，恒为 `out`） */
  direction: RowDirection;
  /** 转账两端（`kind='transfer'` 时非空） */
  transfer: TransferRow | null;
  /** 是否含「不计收支」的腿 —— UI 据此标注（`AC-C1.7`：转账与其它不参与收支的腿必须标注） */
  hasExcludedLegs: boolean;
  /** 主账户定位是否退化（见文件头「退化形态」）—— 消费端应显示可解释提示，不得静默 */
  ambiguous: boolean;
}

// ───────────────────────── 有效「计入收支」判定 ─────────────────────────

/**
 * 该腿是否**计入收支**。
 *
 * 🔴 两处标记取 **OR**（`legs.excludeFromStats` ∨ `transactions.excludeFromStats`）——
 * 架构 §4.1 两个层级都有这个标记（交易级用于"整笔不进统计"，腿级用于"这条腿不进统计"），
 * 凡"计入 / 不计入"的消费方都必须按本函数判定（导出供 K 域 / 预算 / 报表复用）。
 */
export function isCountedInStats(leg: Pick<RowLeg, 'excludeFromStats' | 'txnExcludeFromStats'>): boolean {
  return !leg.excludeFromStats && !leg.txnExcludeFromStats;
}

// ─────────────────────────── 内部工具 ───────────────────────────

interface AccountGroup {
  accountId: string;
  currency: string;
  legs: RowLeg[];
  hasIn: boolean;
  hasOut: boolean;
  countedLegs: number;
}

function groupByAccount(legs: readonly RowLeg[]): Map<string, AccountGroup> {
  const map = new Map<string, AccountGroup>();
  for (const leg of legs) {
    let g = map.get(leg.accountId);
    if (g === undefined) {
      g = {
        accountId: leg.accountId,
        currency: leg.currency,
        legs: [],
        hasIn: false,
        hasOut: false,
        countedLegs: 0,
      };
      map.set(leg.accountId, g);
    }
    g.legs.push(leg);
    if (leg.direction === 'in') g.hasIn = true;
    else g.hasOut = true;
    if (isCountedInStats(leg)) g.countedLegs += 1;
  }
  return map;
}

/**
 * 从候选里挑「腿数最多、并列取 `accountId` 字典序最小」的那个 —— **确定性选择**。
 *
 * 🔴 为什么必须确定性：同一个查询每次都要给出同一个父行，否则「翻页时同一笔账金额变了」
 * 这类现象会直接摧毁用户对账的信任（`AC-C1.4` 的稳定序是同一诉求）。
 */
function pickDominant(groups: readonly AccountGroup[]): AccountGroup {
  return groups.reduce((best, cur) => {
    if (cur.legs.length !== best.legs.length) return cur.legs.length > best.legs.length ? cur : best;
    return cur.accountId < best.accountId ? cur : best;
  });
}

/** 某账户上的净腿变动 = Σin − Σout（同币种；整数分加法） */
function netOf(group: AccountGroup): Cents {
  const ins: Cents[] = [];
  const outs: Cents[] = [];
  for (const leg of group.legs) {
    if (leg.direction === 'in') ins.push(leg.amount);
    else outs.push(leg.amount);
  }
  return toCents((sumCents(ins) as number) - (sumCents(outs) as number));
}

function directionOf(net: Cents): RowDirection {
  const n = net as number;
  return n > 0 ? 'in' : n < 0 ? 'out' : 'flat';
}

function sumByDirection(group: AccountGroup, direction: LegDirection): Cents {
  const list: Cents[] = [];
  for (const leg of group.legs) {
    if (leg.direction === direction) list.push(leg.amount);
  }
  return sumCents(list);
}

// ───────────────────────────── 导出 ─────────────────────────────

/**
 * 派生父行。**唯一入口**（服务端列表 / 详情 / Web / 小程序都调它）。
 *
 * @param legs 该交易的**全部**腿（顺序无关，内部自会分组）
 * @throws RangeError 当 `legs` 为空 —— 一笔交易至少 1 条腿（A 域 §5.2 的资金事件映射表无零腿形态）
 */
export function deriveParentRow(legs: readonly RowLeg[]): ParentRow {
  if (legs.length === 0) {
    throw new RangeError('父行派生需要至少 1 条腿（零腿交易不属任何已建模形态）');
  }

  const groups = groupByAccount(legs);
  const groupList = [...groups.values()];
  const hasExcludedLegs = legs.some((l) => !isCountedInStats(l));

  // ── ① 转账：跨账户、方向相反、**全部腿都不计收支** ──
  //    最后一条件是判别关键：工资条同样跨账户、也有 in 与 out，但它的收入 / 支出腿**计入收支**。
  //    转账的每一腿都是资金搬运（`DR-004`：跨币种两腿均标不计收支），故全部腿不计收支。
  const noneCounted = legs.every((l) => !isCountedInStats(l));
  if (groupList.length >= 2 && noneCounted) {
    const outGroups = groupList.filter((g) => g.hasOut && !g.hasIn);
    const inGroups = groupList.filter((g) => g.hasIn && !g.hasOut);
    // 两端都必须恰好找得到；否则是"多出口 / 多入口"的未建模形态 ⇒ 退化为净额分支
    if (outGroups.length >= 1 && inGroups.length >= 1) {
      const from = pickDominant(outGroups);
      const to = pickDominant(inGroups);
      return {
        kind: ParentRowKind.Transfer,
        mainAccountId: null,
        currency: from.currency,
        net: null,
        direction: 'out',
        transfer: {
          fromAccountId: from.accountId,
          fromCurrency: from.currency,
          fromAmount: sumByDirection(from, 'out'),
          toAccountId: to.accountId,
          toCurrency: to.currency,
          toAmount: sumByDirection(to, 'in'),
          sameCurrency: from.currency === to.currency,
        },
        hasExcludedLegs,
        ambiguous: outGroups.length > 1 || inGroups.length > 1,
      };
    }
  }

  // ── ② 主账户定位：优先「同时有 in 与 out 的账户」（= `DR-016` §3.8③ 的"唯一出口"）──
  const bothDirections = groupList.filter((g) => g.hasIn && g.hasOut);
  const ambiguous = bothDirections.length === 0 && groupList.length > 1;
  const main =
    bothDirections.length > 0
      ? pickDominant(bothDirections)
      : groupList.length === 1
        ? (groupList[0] as AccountGroup)
        : pickDominant(groupList);

  const net = netOf(main);
  return {
    // 🔴 「单腿」判的是**整个交易**只有 1 条腿，不是"主账户上只有 1 条腿" ——
    //    跨账户各出一条腿的交易（退化形态）在主账户上也是 1 条腿，但它显然不是单腿交易。
    kind: legs.length === 1 ? ParentRowKind.Single : ParentRowKind.AccountNet,
    mainAccountId: main.accountId,
    currency: main.currency,
    net,
    direction: directionOf(net),
    transfer: null,
    hasExcludedLegs,
    ambiguous: ambiguous || bothDirections.length > 1,
  };
}

// ─────────────────── 退款后的「净额」（批 `120` · `DR-069` D1） ───────────────────

/**
 * 一笔消费在**流水行上**该显示的净额 —— 唯一实现（`DR-069` D1）。
 *
 * ```
 * 净额 = 本笔净额 + 该笔退款合计        // net 为负（流出）、refunded 为正
 * ```
 *
 * ## 🔴 为什么不直接改 `parent.net`
 *
 * `net` 是"**这一笔交易的腿净额**"，是绝对事实：可退额度、对账、导出都在消费它。
 * 本函数只派生一个**展示值** —— 让 `net` 保持原义，避免"同一事实两个载体"互相打架（铁律 24）。
 *
 * ## 🔴 为什么不再减优惠（`DR-069` D2）
 *
 * `DR-040` 已定：`amount` 恒为**实付净额**，`discount`（优惠）只作记录、不参与任何聚合，
 * 原价 = `amount + discount` 是派生量。⇒ `净额 = amount − 已退`，与 `discount` 无关。
 *
 * 再减一次会怎样：「原价 176 / 优惠 0.50 / 实付 175.50 / 退 50」会被算成 **125.00** ——
 * 凭空多扣 0.50，而且卡面与报表会对不上（报表那条口径也只减冲减腿）。
 *
 * @param net      本笔净额（`parent.net`，流出为负）
 * @param refunded 该笔退款合计（正数，`refunds.amount`）
 */
export function netAfterRefund(net: Cents, refunded: Cents): Cents {
  return toCents((net as number) + (refunded as number));
}
